import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleById, articles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

const articleImageMap: Record<string, string> = {
  'article-wcq-2026': 'https://readdy.ai/api/search-image?query=Professional%20soccer%20match%20scene%20with%20players%20in%20action%20on%20green%20field%2C%20dramatic%20stadium%20lighting%2C%20warm%20golden%20tones%2C%20editorial%20sports%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20cinematic%20motion%20blur%2C%20clean%20background&width=1200&height=500&seq=article-wcq-2026&orientation=landscape',
  'article-nba-preview': 'https://readdy.ai/api/search-image?query=Dynamic%20basketball%20game%20action%20with%20player%20dunking%2C%20dramatic%20arena%20lighting%2C%20warm%20orange%20and%20red%20tones%2C%20editorial%20sports%20photography%2C%20motion%20capture%2C%20clean%20background%2C%20professional%20composition&width=1200&height=500&seq=article-nba-preview&orientation=landscape',
  'article-wimbledon-2026': 'https://readdy.ai/api/search-image?query=Elegant%20tennis%20match%20on%20grass%20court%20with%20player%20serving%2C%20warm%20sunlight%2C%20classic%20Wimbledon%20aesthetic%2C%20editorial%20sports%20photography%2C%20shallow%20depth%20of%20field%2C%20clean%20green%20background&width=1200&height=500&seq=article-wimbledon-2026&orientation=landscape',
  'article-lol-s16': 'https://readdy.ai/api/search-image?query=Epic%20esports%20arena%20with%20dramatic%20stage%20lighting%20and%20large%20screens%2C%20competitive%20gaming%20atmosphere%2C%20neon%20red%20and%20gold%20accents%2C%20cinematic%20wide%20shot%2C%20crowded%20venue%2C%20professional%20photography&width=1200&height=500&seq=article-lol-s16&orientation=landscape',
  'article-ucl-draw': 'https://readdy.ai/api/search-image?query=European%20champions%20league%20trophy%20and%20stadium%20atmosphere%2C%20dramatic%20night%20lighting%2C%20golden%20and%20deep%20red%20tones%2C%20editorial%20sports%20photography%2C%20cinematic%20composition%2C%20star%20field%20lights&width=1200&height=500&seq=article-ucl-draw&orientation=landscape',
  'article-register-guide': 'https://readdy.ai/api/search-image?query=Modern%20digital%20sports%20platform%20interface%20with%20warm%20lighting%2C%20sleek%20design%20aesthetic%2C%20red%20and%20gold%20accents%2C%20clean%20professional%20look%2C%20abstract%20sports%20background%2C%20soft%20gradients&width=1200&height=500&seq=article-register-guide&orientation=landscape',
  'article-csl-midseason': 'https://readdy.ai/api/search-image?query=Chinese%20Super%20League%20soccer%20match%20with%20players%20competing%2C%20packed%20stadium%2C%20dramatic%20daylight%2C%20warm%20golden%20hour%20lighting%2C%20editorial%20sports%20photography%2C%20vibrant%20atmosphere&width=1200&height=500&seq=article-csl-midseason&orientation=landscape',
  'article-fiba-china': 'https://readdy.ai/api/search-image?query=Chinese%20national%20basketball%20team%20in%20action%20during%20international%20game%2C%20dramatic%20arena%20lighting%2C%20red%20and%20gold%20team%20colors%2C%20editorial%20sports%20photography%2C%20motion%20capture%2C%20professional%20composition&width=1200&height=500&seq=article-fiba-china&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article: Article | undefined = useMemo(() => (id ? getArticleById(id) : undefined), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-800">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline cursor-pointer">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Organization', name: article.author },
    datePublished: article.date,
    url: `https://www.kaiyunsports.com/article/${article.id}`,
    publisher: { '@type': 'Organization', name: '开云体育平台' },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <article className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: `${article.category}赛事`, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-500">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                <span><i className="ri-eye-line mr-1"></i>{article.readCount.toLocaleString()} 阅读</span>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={articleImageMap[article.imageSeq]}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-14 pt-10 border-t border-background-200">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={articleImageMap[ra.imageSeq] || articleImageMap['article-wcq-2026']}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={ra.title}
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="mt-2 text-xs text-foreground-500">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}